import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';
import AnalyzerHeader from './AnalyzerHeader';
import ScoreCard from './ScoreCard';
import ActionsTable from './ActionsTable';
import { isFiniteNumber } from './scoreUtils';
import NeighborDataChipConnected from './NeighborDataChipConnected';
import { NeighborDataIncompleteCard } from './NeighborDataStatus';
import { useAnalyserGate } from './useAnalyserGate';
import { useNeighborData } from './NeighborDataContext';
import PowerMapView from './PowerMapView';
import {
  applyLinkVisualSpread,
  buildMapLinks,
  buildMapLinksFromTopology,
  DEFAULT_POWER_FILTERS,
  extractDeviceGPS,
  extractDeviceMarkers,
  filterLinks,
  formatTxChange,
  formatPowerDelta,
  isPowerRaise,
  normalizeSerial,
  parseRssiFilterInput,
  POWER_POLICY_DEFAULTS,
} from '../../utils/powerMapUtils';

const POWER_PLAN_CATALOG = {
  id: 'power-plan',
  title: 'Power plan',
  short: 'Tiered TX-power audit — lower toward −75, raise only below −79',
  icon: BoltIcon,
  color: 'primary',
  isMeta: false,
  tooltip:
    'Holistic TX-power plan: trim over-powered radios toward the operating tier (−75 dBm), raise only when RSSI drops below the coverage floor (−79 dBm), capped at 18 dBm and +3 dB per step.',
  description: (
    <>
      Tiered, power-first TX audit on every child→parent link (STA uplink + AP downlink).
      <ul style={{ margin: '4px 0 4px', paddingLeft: 18 }}>
        <li><strong>Lower</strong> — over-powered radios trimmed so the worst receiver lands near the operating tier (−75 dBm).</li>
        <li><strong>Raise</strong> — only when RSSI &lt; −79 dBm; targets the coverage floor, max +3 dB per plan, 18 dBm cap.</li>
        <li><strong>Intra-device</strong> — STA/AP (or dual CAP slots) kept within 2 dB on the same device.</li>
        <li><strong>Run / Apply</strong> — audit on the map, then PATCH recommended <code>tx_power_up</code> / <code>tx_power_down</code>.</li>
      </ul>
    </>
  ),
  algorithm: 'Power-first tiers: lower → operating tier (−75); raise → coverage floor (−79) if RSSI < −79; max 18 dBm, +3 dB/step',
  measures: 'Total TX power in mW (lower is better). Before → after shows aggregate savings (raises increase mW where needed).',
  actsOn: 'PATCH /tree-mgmt/{serial}/config — sets tx_power_down or tx_power_up.',
};

const formatRSSI = (value) =>
  isFiniteNumber(value) ? `${value.toFixed(0)} dBm` : '—';

const formatPolicyTier = (paramsUsed) => {
  const op = paramsUsed?.operating_tier_dbm ?? POWER_POLICY_DEFAULTS.operatingTierDbm;
  const cov = paramsUsed?.coverage_floor_dbm ?? POWER_POLICY_DEFAULTS.coverageFloorDbm;
  const max = paramsUsed?.max_recommend_dbm ?? POWER_POLICY_DEFAULTS.maxRecommendDbm;
  return { op, cov, max };
};

const summarizePowerActions = (actions) => {
  const list = actions || [];
  const lowers = list.filter((a) => !isPowerRaise(a)).length;
  const raises = list.filter((a) => isPowerRaise(a)).length;
  return { lowers, raises, total: list.length };
};

const PowerPlanPanel = ({ devices, isActive }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { refresh: refreshDataStatus } = useNeighborData();
  const { missingSerials, clearMissing, runGuarded, scanMissing, scanningSerials } = useAnalyserGate();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_POWER_FILTERS);
  const [minRssiAPInput, setMinRssiAPInput] = useState(String(DEFAULT_POWER_FILTERS.minRssiAP));
  const [minRssiSTAInput, setMinRssiSTAInput] = useState(String(DEFAULT_POWER_FILTERS.minRssiSTA));
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [busyActionId, setBusyActionId] = useState(null);
  const [applyAllBusy, setApplyAllBusy] = useState(false);

  const gpsMap = useMemo(() => extractDeviceGPS(devices), [devices]);

  const allMapLinks = useMemo(() => {
    if (!result) return [];
    let links;
    if (Array.isArray(result.links) && result.links.length > 0) {
      links = buildMapLinks(result.links, gpsMap);
    } else {
      links = buildMapLinksFromTopology(devices, gpsMap, result.summary?.device_tx_power);
    }
    return applyLinkVisualSpread(links);
  }, [result, gpsMap, devices]);

  const effectiveFilters = useMemo(() => ({
    ...filters,
    minRssiAP: parseRssiFilterInput(minRssiAPInput, filters.minRssiAP),
    minRssiSTA: parseRssiFilterInput(minRssiSTAInput, filters.minRssiSTA),
  }), [filters, minRssiAPInput, minRssiSTAInput]);

  const visibleLinks = useMemo(
    () => filterLinks(allMapLinks, effectiveFilters, devices),
    [allMapLinks, effectiveFilters, devices],
  );

  const deviceMarkers = useMemo(
    () => extractDeviceMarkers(devices, gpsMap, visibleLinks),
    [devices, gpsMap, visibleLinks],
  );

  const deviceOptions = useMemo(() => {
    if (!devices) return [];
    return devices
      .filter((d) => d.serial_number)
      .map((d) => d.serial_number)
      .sort();
  }, [devices]);

  const selectedDeviceOptions = useMemo(
    () => deviceOptions.filter((serial) => filters.deviceSerials.has(normalizeSerial(serial))),
    [deviceOptions, filters.deviceSerials],
  );

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    const outcome = await runGuarded(() =>
      api.getPowerAnalysisWithParams({ includeLinks: true }),
    );
    setLoading(false);
    if (outcome.ok) {
      clearMissing();
      setResult(outcome.data);
      const { lowers, raises, total } = summarizePowerActions(outcome.data?.actions);
      const linkCount = outcome.data?.links?.length || 0;
      let msg = `Power plan: ${linkCount} links audited`;
      if (total === 0) {
        msg += ', no changes needed.';
      } else if (lowers > 0 && raises > 0) {
        msg += `, ${lowers} lower(s) and ${raises} raise(s) recommended.`;
      } else if (raises > 0) {
        msg += `, ${raises} raise(s) recommended.`;
      } else {
        msg += `, ${lowers} lower(s) recommended.`;
      }
      enqueueSnackbar(msg, { variant: total === 0 ? 'success' : 'info' });
      return;
    }
    if (outcome.gated) {
      setResult(null);
      enqueueSnackbar('Power plan cannot run: neighbour data is incomplete.', { variant: 'error' });
      return;
    }
    enqueueSnackbar(outcome.error?.message || 'Failed to run power analysis', { variant: 'error' });
  }, [enqueueSnackbar, runGuarded, clearMissing]);

  const handleScanMissing = useCallback(async () => {
    const summary = await scanMissing();
    if (!summary) return;
    enqueueSnackbar(
      summary.failed === 0
        ? `Triggered neighbour scan for ${summary.accepted} device(s).`
        : `Triggered ${summary.accepted} scan(s); ${summary.failed} failed to start.`,
      { variant: summary.failed === 0 ? 'info' : 'warning' },
    );
    refreshDataStatus();
  }, [scanMissing, enqueueSnackbar, refreshDataStatus]);

  const applyAction = useCallback(async (action) => {
    if (!action?.endpoint) return;
    setBusyActionId(action.action_id);
    try {
      await api.executeAnalyzerAction(action);
      enqueueSnackbar(
        `${action.serial} ${action.radio_name}: ${action.current_tx_power} → ${action.recommended_tx_power} dBm`,
        { variant: 'success' },
      );
      await runAnalysis();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to apply action', { variant: 'error' });
    } finally {
      setBusyActionId(null);
    }
  }, [enqueueSnackbar, runAnalysis]);

  const applyAll = useCallback(async () => {
    if (!result?.actions?.length) return;
    setApplyAllBusy(true);
    let applied = 0;
    let hadError = false;
    try {
      for (const action of result.actions) {
        // eslint-disable-next-line no-await-in-loop
        await api.executeAnalyzerAction(action);
        applied += 1;
      }
      enqueueSnackbar(`Applied ${applied} TX-power action(s).`, { variant: 'success' });
    } catch (error) {
      hadError = true;
      enqueueSnackbar(`${applied} applied; stopped on error: ${error?.message || 'unknown'}`, { variant: 'error' });
    } finally {
      setApplyAllBusy(false);
      if (applied > 0 || hadError) {
        await runAnalysis();
      }
    }
  }, [result, enqueueSnackbar, runAnalysis]);

  useEffect(() => {
    if (selectedLinkId && !visibleLinks.some((l) => l.id === selectedLinkId)) {
      setSelectedLinkId(null);
    }
  }, [visibleLinks, selectedLinkId]);

  const enableWeakRssiMapMode = useCallback(() => {
    setFilters((f) => (f.showMode === 'weak_rssi' ? f : { ...f, showMode: 'weak_rssi' }));
  }, []);

  const commitMinRssiAP = useCallback(() => {
    const v = parseRssiFilterInput(minRssiAPInput, null);
    if (v != null) {
      setFilters((f) => ({ ...f, minRssiAP: v, showMode: 'weak_rssi' }));
      setMinRssiAPInput(String(v));
      return;
    }
    setMinRssiAPInput(String(filters.minRssiAP));
  }, [minRssiAPInput, filters.minRssiAP]);

  const commitMinRssiSTA = useCallback(() => {
    const v = parseRssiFilterInput(minRssiSTAInput, null);
    if (v != null) {
      setFilters((f) => ({ ...f, minRssiSTA: v, showMode: 'weak_rssi' }));
      setMinRssiSTAInput(String(v));
      return;
    }
    setMinRssiSTAInput(String(filters.minRssiSTA));
  }, [minRssiSTAInput, filters.minRssiSTA]);

  const summary = result?.summary;
  const actions = result?.actions || [];
  const paramsUsed = result?.params_used;
  const policy = formatPolicyTier(paramsUsed);

  const columns = [
    {
      key: 'direction',
      label: 'Arm',
      render: (a) => {
        const isUplink = a.direction === 'uplink';
        const isRaise = isPowerRaise(a);
        const tip = isUplink
          ? (isRaise
            ? 'Uplink — raising this child\'s STA TX toward the coverage floor (−79 dBm).'
            : 'Uplink — lowering this child\'s STA TX toward the operating tier (−75 dBm).')
          : (isRaise
            ? 'Downlink — raising this AP\'s TX toward the coverage floor (−79 dBm).'
            : 'Downlink — lowering this AP\'s TX toward the operating tier (−75 dBm).');
        return (
          <Tooltip title={tip}>
            <Chip
              size="small"
              label={isUplink ? '↑ STA' : '↓ AP'}
              color={isRaise ? 'warning' : (isUplink ? 'warning' : 'primary')}
              variant={isRaise ? 'filled' : 'outlined'}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'radio',
      label: 'Device / Radio',
      render: (a) => (
        <Stack spacing={0}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.serial}</Typography>
          <Typography variant="caption" color="text.secondary">
            {a.radio_name} ({a.band})
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'power',
      label: 'TX power',
      render: (a) => (
        <Typography variant="body2">
          {formatTxChange(a.current_tx_power, a.recommended_tx_power)}
        </Typography>
      ),
    },
    {
      key: 'delta',
      label: 'Change',
      render: (a) => {
        const isRaise = isPowerRaise(a);
        return (
          <Chip
            size="small"
            color={isRaise ? 'warning' : 'success'}
            variant={isRaise ? 'filled' : 'outlined'}
            label={formatPowerDelta(a)}
          />
        );
      },
    },
    {
      key: 'flags',
      label: 'Flags',
      render: (a) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
          {a.coverage_gap && (
            <Tooltip title="Even at the radio's max TX (18 dBm hardware cap, or lower when a regulatory EIRP cap minus antenna gain binds tighter) the link cannot reach the coverage floor — best effort only.">
              <Chip size="small" color="error" variant="outlined" label="Coverage gap" />
            </Tooltip>
          )}
          {a.coupling_forced && (
            <Tooltip title="Aligned with other radios on this device to stay within the 2 dB shared power-domain limit. Reconciliation targets the binding-side maximum (the link-safe ceiling), not the arithmetic mean — protects coupled radios from being dropped below their per-link minimum.">
              <Chip size="small" color="info" variant="outlined" label="Coupling" />
            </Tooltip>
          )}
          {!a.coverage_gap && !a.coupling_forced && (
            <Typography variant="caption" color="text.secondary">—</Typography>
          )}
        </Stack>
      ),
    },
    {
      key: 'rssi',
      label: 'Receiver RSSI',
      render: (a) => {
        const isUplink = a.direction === 'uplink';
        const rssi = isUplink
          ? a.children?.[0]?.rssi_down_dbm
          : a.worst_child_rssi ?? a.children?.[0]?.rssi_down_dbm;
        const label = isUplink ? 'at parent' : 'at child';
        return (
          <Tooltip title={isUplink
            ? 'Parent hears this child\'s STA at this RSSI (ack_signal_avg).'
            : 'Weakest child hears this AP at this RSSI.'}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${formatRSSI(rssi)} ${label}`}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'reason',
      label: 'Why',
      render: (a) => (
        <Typography variant="caption" color="text.secondary">{a.reason}</Typography>
      ),
    },
  ];

  const hasGPS = gpsMap.size > 0;

  if (!isActive) return null;

  return (
    <Box>
      <AnalyzerHeader
        catalog={POWER_PLAN_CATALOG}
        loading={loading}
        onRun={runAnalysis}
        hasResult={!!result}
        rightContent={<NeighborDataChipConnected />}
      />

      {missingSerials.length > 0 && (
        <NeighborDataIncompleteCard
          missingSerials={missingSerials}
          onScanMissing={handleScanMissing}
          scanningSerials={scanningSerials}
        />
      )}

      {/* Controls bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              Tier policy (fixed)
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75, mb: 0.5 }}>
              <Chip size="small" color="success" variant="outlined" label={`Lower → ${policy.op} dBm`} />
              <Chip size="small" color="warning" variant="outlined" label={`Raise if < ${policy.cov} dBm`} />
              <Chip size="small" variant="outlined" label={`Max ${policy.max} dBm · +3 dB/step`} />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.45 }}
            >
              Lowers trim over-powered radios toward the operating tier. Raises apply only when receiver RSSI
              drops below the coverage floor, capped at 18 dBm and +3 dB per plan.
              {paramsUsed && ` Intra-device coupling: ≤ ${paramsUsed.intra_device_max_delta_db ?? 2} dB.`}
            </Typography>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Band</InputLabel>
              <Select
                value={filters.bandFilter}
                label="Band"
                onChange={(e) => setFilters((f) => ({ ...f, bandFilter: e.target.value }))}
              >
                <MenuItem value="all">All bands</MenuItem>
                <MenuItem value="2.4GHz">2.4 GHz</MenuItem>
                <MenuItem value="5GHz">5 GHz</MenuItem>
                <MenuItem value="6GHz">6 GHz</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <Tooltip title="Show links where AP downlink RSSI at the child is below this value. Switches the map to Weak RSSI mode.">
              <TextField
                label="Min RSSI AP"
                type="text"
                inputMode="numeric"
                size="small"
                fullWidth
                value={minRssiAPInput}
                onChange={(e) => {
                  setMinRssiAPInput(e.target.value);
                  enableWeakRssiMapMode();
                }}
                onBlur={commitMinRssiAP}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitMinRssiAP();
                }}
                helperText={filters.showMode === 'weak_rssi' ? 'Map: Weak RSSI' : 'Select Weak RSSI on map'}
                FormHelperTextProps={{ sx: { fontSize: '0.65rem', m: 0, mt: 0.25 } }}
                InputProps={{ endAdornment: <Typography variant="caption">dBm</Typography> }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6} md={2}>
            <Tooltip title="Show links where STA uplink RSSI at the parent is below this value. Switches the map to Weak RSSI mode.">
              <TextField
                label="Min RSSI STA"
                type="text"
                inputMode="numeric"
                size="small"
                fullWidth
                value={minRssiSTAInput}
                onChange={(e) => {
                  setMinRssiSTAInput(e.target.value);
                  enableWeakRssiMapMode();
                }}
                onBlur={commitMinRssiSTA}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitMinRssiSTA();
                }}
                helperText={filters.showMode === 'weak_rssi' ? 'Map: Weak RSSI' : 'Select Weak RSSI on map'}
                FormHelperTextProps={{ sx: { fontSize: '0.65rem', m: 0, mt: 0.25 } }}
                InputProps={{ endAdornment: <Typography variant="caption">dBm</Typography> }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={4}>
            <Tooltip title="Show all backhaul links for the selected device. For CAPs, includes direct children and the full cap_serial tree. Overrides the map link mode filter.">
              <Autocomplete
                multiple
                size="small"
                options={deviceOptions}
                value={selectedDeviceOptions}
                isOptionEqualToValue={(option, value) => (
                  option.toLowerCase() === value.toLowerCase()
                )}
                onChange={(_e, val) => setFilters((f) => ({
                  ...f,
                  deviceSerials: new Set(val.map((s) => normalizeSerial(s))),
                }))}
                renderInput={(params) => (
                  <TextField {...params} label="Filter devices" placeholder="Serial..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, idx) => (
                    <Chip size="small" label={option} {...getTagProps({ index: idx })} key={option} />
                  ))
                }
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Map */}
      {result && hasGPS && (
        <Paper sx={{ p: 1, mb: 2 }}>
          <PowerMapView
            devices={devices}
            mapLinks={visibleLinks}
            deviceMarkers={deviceMarkers}
            selectedLinkId={selectedLinkId}
            onSelectLink={setSelectedLinkId}
            height={520}
            showMode={effectiveFilters.showMode}
            onShowModeChange={(mode) => setFilters((f) => ({ ...f, showMode: mode }))}
          />
        </Paper>
      )}

      {result && !hasGPS && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Map unavailable — {devices?.length || 0} devices loaded but none have GPS coordinates
          (gps or gps_location on device, or inherited from jnet2 parent for AI nodes).
          Link data is shown in the table below.
        </Alert>
      )}

      {result && hasGPS && allMapLinks.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {gpsMap.size} device(s) have GPS but no backhaul links could be drawn.
          Check that child devices have a parent with GPS, or wait for the backend to return link audit rows.
        </Alert>
      )}

      {/* Score + summary */}
      {result && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ScoreCard score={result.score} />
          </Box>
          <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Audit snapshot
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
              <Chip size="small" label={`${summary?.total_devices ?? 0} devices`} />
              <Chip size="small" color="primary" label={`${summary?.radios_analyzed ?? 0} radios`} />
              <Chip
                size="small"
                color={summary?.actions_emitted > 0 ? 'success' : 'default'}
                label={`${summary?.actions_emitted ?? 0} actions`}
              />
              <Chip size="small" variant="outlined" label={`${result?.links?.length ?? 0} links`} />
              <Chip size="small" variant="outlined" label={`${result?.issues?.length ?? 0} issues`} />
            </Stack>
            {paramsUsed && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Operating tier: {paramsUsed.operating_tier_dbm ?? policy.op} dBm · Coverage floor: {paramsUsed.coverage_floor_dbm ?? policy.cov} dBm · Max TX: {paramsUsed.max_recommend_dbm ?? policy.max} dBm
              </Typography>
            )}
          </Paper>
        </Stack>
      )}

      {/* Actions table */}
      {result && actions.length > 0 && (
        <ActionsTable
          actions={actions}
          columns={columns}
          onApply={applyAction}
          busyActionId={busyActionId}
          onApplyAll={applyAll}
          applyAllBusy={applyAllBusy}
          applyAllLabel={`Apply all (${actions.length})`}
          emptyText="No TX-power changes recommended."
        />
      )}

      {result && actions.length === 0 && (
        <Alert severity="success">
          All audited radios meet the tier policy — no lowers or raises recommended
          (operating tier {policy.op} dBm, coverage floor {policy.cov} dBm).
        </Alert>
      )}

      {!result && !loading && (
        <Alert severity="info">
          Click <strong>Run</strong> to audit TX power across all backhaul links.
          The plan lowers toward {POWER_POLICY_DEFAULTS.operatingTierDbm} dBm and raises only when RSSI drops below {POWER_POLICY_DEFAULTS.coverageFloorDbm} dBm.
        </Alert>
      )}
    </Box>
  );
};

export default PowerPlanPanel;
